import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import axios from 'axios'
import { getAccessToken } from '../Auth/AuthStore'

import styles from './ResultPage.module.css'
import medalIcon from '../../assets/icons/medalPurple.svg'
import pictureIcon from '../../assets/icons/picture.svg'

const ProgramResultPage = () => {
  const { therapyProgramId } = useParams()
  const navigate = useNavigate()

  const [scores, setScores] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    const fetchScores = async () => {
      try {
        setLoading(true)
        setError(null)

        const token = getAccessToken()
        if (!token) throw new Error('로그인 토큰이 없습니다.')

        const res = await axios.get(
          `${import.meta.env.VITE_API_BASE}/api/programs/${therapyProgramId}/scores`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )

        const scoreList = res.data?.data?.scores || []
        setScores(scoreList)
      } catch (e) {
        console.error('코스 결과 조회 실패:', e)
        setError('코스 결과를 불러오는 중 오류가 발생했습니다.')
      } finally {
        setLoading(false)
      }
    }

    if (therapyProgramId) {
      fetchScores()
    } else {
      setLoading(false)
      setError('치유 프로그램 정보를 찾을 수 없습니다.')
    }
  }, [therapyProgramId])

  const handleBackToDashboard = () => {
    navigate('/dashboard')
  }

  if (loading) {
    return (
      <div className={styles.resultPage}>
        <div className={styles.container}>
          <p>코스 결과를 불러오는 중입니다...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={styles.resultPage}>
        <div className={styles.container}>
          <p>{error}</p>
          <button className={styles.mainBtn} onClick={handleBackToDashboard}>
            대시보드로 돌아가기
          </button>
        </div>
      </div>
    )
  }

  if (!scores.length) {
    return (
      <div className={styles.resultPage}>
        <div className={styles.container}>
          <p>표시할 코스 결과가 없습니다.</p>
          <button className={styles.mainBtn} onClick={handleBackToDashboard}>
            대시보드로 돌아가기
          </button>
        </div>
      </div>
    )
  }

  const totalRounds = scores.length
  const averageScore =
    scores.reduce((sum, item) => sum + (item.score ?? 0), 0) /
    (totalRounds || 1)

  return (
    <div className={styles.resultPage}>
      <div className={styles.container}>
        <header className={styles.pageHeader}>
          <div className={styles.headerIcon}>
            <img src={medalIcon} alt="medal" />
          </div>
          <h1>치유 코스 결과</h1>
        </header>

        {/* 코스 요약 섹션 */}
        <section className={styles.programScoreSection}>
          <div className={styles.scoreCard}>
            <div className={styles.scoreCircle}>
              <svg viewBox="0 0 200 200" className={styles.scoreSvg}>
                <circle
                  cx="100"
                  cy="100"
                  r="85"
                  fill="none"
                  stroke="#e5e7eb"
                  strokeWidth="15"
                />
                <circle
                  cx="100"
                  cy="100"
                  r="85"
                  fill="none"
                  stroke="#7c3aed"
                  strokeWidth="15"
                  strokeDasharray={`${(averageScore / 100) * 534} 534`}
                  strokeDashoffset="0"
                  transform="rotate(-90 100 100)"
                  strokeLinecap="round"
                />
              </svg>
              <div className={styles.scoreText}>
                <span className={styles.scoreNumber}>
                  {Math.round(averageScore)}
                </span>
                <span className={styles.scoreTotal}>/100</span>
              </div>
            </div>
            <h3 className={styles.scoreTitle}>코스 평균 점수</h3>
            <p className={styles.scoreDescription}>
              총 {totalRounds}일 동안의 AI 분석 점수 평균입니다.
            </p>
          </div>

          <div className={styles.analysisCards}>
            <h3 className={styles.analysisTitle}>일차별 점수 변화</h3>
            <div className={styles.scoreChartContainer}>
              <div className={styles.scoreChart}>
                <svg width="100%" height="200" viewBox="0 0 600 200">
                  {/* 배경 그리드 */}
                  {[0, 20, 40, 60, 80, 100].map((value) => (
                    <g key={value}>
                      <line
                        x1="50"
                        y1={180 - (value * 1.6)}
                        x2="550"
                        y2={180 - (value * 1.6)}
                        stroke="#f0f0f0"
                        strokeWidth="1"
                      />
                      <text
                        x="30"
                        y={185 - (value * 1.6)}
                        fontSize="12"
                        fill="#999"
                        textAnchor="end"
                      >
                        {value}
                      </text>
                    </g>
                  ))}
                  
                  {/* 차트 라인 */}
                  <path
                    d={scores.map((item, index) => {
                      const x = 70 + (index * 120)
                      const y = 180 - ((item.score ?? 0) * 1.6)
                      return `${index === 0 ? 'M' : 'L'} ${x} ${y}`
                    }).join(' ')}
                    fill="none"
                    stroke="#7c3aed"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  
                  {/* 차트 포인트 */}
                  {scores.map((item, index) => {
                    const x = 70 + (index * 120)
                    const y = 180 - ((item.score ?? 0) * 1.6)
                    return (
                      <g key={item.dailyCourseId}>
                        <circle
                          cx={x}
                          cy={y}
                          r="5"
                          fill="#7c3aed"
                          stroke="white"
                          strokeWidth="2"
                        />
                        {/* 점수 텍스트 */}
                        <text
                          x={x}
                          y={y - 12}
                          fontSize="12"
                          fill="#7c3aed"
                          fontWeight="600"
                          textAnchor="middle"
                        >
                          {item.score ?? 0}
                        </text>
                      </g>
                    )
                  })}
                  
                  {/* 채우기 영역 */}
                  <path
                    d={`M 70 180 ${scores.map((item, index) => {
                      const x = 70 + (index * 120)
                      const y = 180 - ((item.score ?? 0) * 1.6)
                      return `L ${x} ${y}`
                    }).join(' ')} L ${70 + ((scores.length - 1) * 120)} 180 Z`}
                    fill="url(#scoreGradient)"
                    opacity="0.3"
                  />
                  
                  {/* 그라디언트 정의 */}
                  <defs>
                    <linearGradient id="scoreGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" stopColor="#7c3aed" stopOpacity="0.4"/>
                      <stop offset="100%" stopColor="#7c3aed" stopOpacity="0"/>
                    </linearGradient>
                  </defs>
                </svg>
              </div>
              
              {/* X축 라벨 */}
              <div className={styles.scoreXAxisLabels}>
                {scores.map((item) => (
                  <span key={item.dailyCourseId} className={styles.scoreXAxisLabel}>
                    {item.round}일차
                  </span>
                ))}
              </div>
              
              <p className={styles.scoreChartDescription}>
                5일간의 코스 점수 변화 추이
              </p>
            </div>
          </div>
        </section>

        {/* 일차별 기록 섹션 */}
        <section className={styles.programDrawingSection}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionIcon}>
              <img src={pictureIcon} alt="picture" />
            </span>
            <h2>일차별 점수와 이미지</h2>
          </div>

          <div className={styles.recommendationsRow}>
            <div className={styles.recommendationsBody}>
                <div className={styles.recommendationsGrid}>
                  {scores.map((item) => (
                    <div
                      key={item.dailyCourseId}
                      className={styles.recommendationCard}
                      onClick={() => {
                        if (item.drawingAnalysisId) {
                          navigate(`/drawing-analysis/${item.drawingAnalysisId}`, {
                            state: { fromProgramResult: true, therapyProgramId }
                          })
                        }
                      }}
                    >
                      <div className={styles.placeCardInner}>
                        <div className={styles.placeImageWrapper}>
                          {item.imageUrl ? (
                            <img
                              src={item.imageUrl}
                              alt={`round-${item.round}`}
                              className={styles.placeImage}
                            />
                          ) : (
                            <div className={styles.placeImagePlaceholder}>
                              이미지 없음
                            </div>
                          )}
                        </div>
                        <div className={styles.placeContent}>
                          <h3 className={styles.roundTitle}>
                            {item.round}일차
                          </h3>
                          <h2 className={styles.placeTitle}>
                            제목 : {item.title}
                          </h2>
                          <p className={styles.placeRating}>
                            점수: {item.score ?? '-'}점
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
            </div>
          </div>
        </section>

        <div className={styles.actionButtons}>
          <button className={styles.mainBtn} onClick={handleBackToDashboard}>
            대시보드로 돌아가기
          </button>
        </div>
      </div>
    </div>
  )
}

export default ProgramResultPage


